import _regeneratorRuntime from "@babel/runtime/regenerator";
import _defineProperty from "@babel/runtime/helpers/defineProperty";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(source, true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(source).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { Value } from 'slate';
import { PeopleIcon, MentionIcon } from '../../components/Icons';
import createStore from '../utils/createStore';
import MentionPicker from './components/Picker';
import { UserStrategy, NoteStrategy } from './strategies';
import isHotKey from 'is-hotkey';
import { defaultState, open, close, select, search, commit, arrowKeyHandler, commitKeyHandler, moveUp, moveDown } from './state';
import insertEntity from '../utils/insertEntity';
import EntityTypes from '../../types/EntityTypes';
import makeToolbarButton, { EXPANDED_INSERTION } from '../utils/makeToolbarButton';

function createMentionPickerPlugin() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      _ref$fetchUsers = _ref.fetchUsers,
      fetchUsers = _ref$fetchUsers === void 0 ? function () {
    return Promise.resolve([]);
  } : _ref$fetchUsers,
      _ref$fetchNotes = _ref.fetchNotes,
      fetchNotes = _ref$fetchNotes === void 0 ? function () {
    return Promise.resolve([]);
  } : _ref$fetchNotes,
      _ref$getCurrentUser = _ref.getCurrentUser,
      getCurrentUser = _ref$getCurrentUser === void 0 ? function () {
    return {
      id: '',
      username: '',
      displayName: ''
    };
  } : _ref$getCurrentUser,
      _ref$bottomSpace = _ref.bottomSpace,
      bottomSpace = _ref$bottomSpace === void 0 ? 0 : _ref$bottomSpace,
      _ref$customStrategies = _ref.customStrategies,
      customStrategies = _ref$customStrategies === void 0 ? [] : _ref$customStrategies,
      _ref$searchSymbol = _ref.searchSymbol,
      searchSymbol = _ref$searchSymbol === void 0 ? '@' : _ref$searchSymbol;

  var store = createStore({
    state: _objectSpread({}, defaultState, {
      searchSymbol: searchSymbol,
      strategies: customStrategies
    })
  });

  function updatePluginState(modifierFn) {
    var payload,
        applyState,
        _args = arguments;
    return _regeneratorRuntime.async(function updatePluginState$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            applyState = function _ref2() {
              var newState = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
              store.updateItem('state', _objectSpread({}, store.getItem('state'), {}, newState));
            };

            payload = _args.length > 1 && _args[1] !== undefined ? _args[1] : {};
            applyState(modifierFn(store.getItem('state'), payload, applyState));

          case 3:
          case "end":
            return _context.stop();
        }
      }
    });
  }

  var throttleInputTimeout;

  var throttleInput = function throttleInput(fn) {
    clearTimeout(throttleInputTimeout);
    throttleInputTimeout = setTimeout(fn, 5);
  };

  function renderEditor(props, editor, next) {
    var children = next();
    return React.createElement(Fragment, null, children, !props.readOnly && React.createElement(MentionPicker, {
      pluginStore: store,
      editor: editor,
      bottomSpace: bottomSpace,
      onSelect: function onSelect(item, index) {
        updatePluginState(select, {
          item: item,
          index: index
        });
      },
      onClick: function onClick(item, index) {
        updatePluginState(commit, {
          item: item,
          index: index,
          editor: editor
        });
      }
    }));
  }

  renderEditor.propTypes = {
    value: PropTypes.instanceOf(Value).isRequired,
    readOnly: PropTypes.bool.isRequired,
    children: PropTypes.node.isRequired
  };
  var strategies = customStrategies.length ? customStrategies : [UserStrategy({
    fetchUsers: fetchUsers,
    getCurrentUser: getCurrentUser
  }), NoteStrategy({
    fetchNotes: fetchNotes
  })];
  return [{
    commands: {
      openMentionPicker: function openMentionPicker(editor) {
        updatePluginState(open, {
          editor: editor,
          strategies: [strategies[0]]
        });
      },
      openNoteLinkPicker: function openNoteLinkPicker(editor) {
        updatePluginState(open, {
          editor: editor,
          strategies: [strategies[1]]
        });
      }
    }
  }, {
    onChange: function onChange(editor, next) {
      if (store.getItem('state').isVisible) {
        throttleInput(function () {
          return updatePluginState(search, {
            editor: editor,
            strategies: strategies
          });
        });
      }

      if (!store.getItem('state').isVisible && !store.getItem('state').searchSymbol) {
        updatePluginState(search, {
          editor: editor,
          strategies: strategies
        });
      }

      return next();
    },
    onKeyDown: function onKeyDown(event, editor, next) {
      if (event.key === '@' && !event.ctrlKey && editor.value.selection.isSet) {
        return throttleInput(function () {
          return updatePluginState(open, {
            editor: editor,
            strategies: strategies
          });
        });
      }

      if (!store.getItem('state').isVisible) {
        return next();
      }

      if (isHotKey('up', event)) {
        return arrowKeyHandler(event, editor.value, store, moveUp);
      }

      if (isHotKey('down', event)) {
        return arrowKeyHandler(event, editor.value, store, moveDown, {
          keyForFirstInteraction: true
        });
      }

      if (isHotKey('tab', event)) {
        return commitKeyHandler(event, editor, store);
      }

      if (isHotKey('enter', event)) {
        if (store.getItem('state').index >= 0 && store.getItem('state').items.length > 0) {
          return commitKeyHandler(event, editor, store);
        }
      }

      if (isHotKey('escape', event)) {
        event.preventDefault();
        updatePluginState(close);
        return true;
      }

      return next();
    },
    renderEditor: renderEditor
  }, makeToolbarButton(EntityTypes.MENTION, {
    icon: MentionIcon,
    title: 'Mention',
    visibility: EXPANDED_INSERTION,
    canApply: function canApply() {
      return Boolean(getCurrentUser());
    },
    onApply: function onApply(editor) {
      editor.insertText('@').openMentionPicker();
    }
  }), makeToolbarButton('me', {
    icon: PeopleIcon,
    title: '@me',
    alias: ['self'],
    visibility: EXPANDED_INSERTION,
    canApply: function canApply() {
      return Boolean(getCurrentUser());
    },
    onApply: function onApply(editor) {
      var _getCurrentUser = getCurrentUser(),
          id = _getCurrentUser.id,
          username = _getCurrentUser.username;

      return insertEntity(editor, null, '@' + username, EntityTypes.MENTION, {
        userId: id
      }).insertText(' ');
    }
  })];
}

export default createMentionPickerPlugin;